import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Button, ButtonGroup, Dropdown, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS } from '../../config/environment'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentListViewService } from '../../services/document-list-view.service'
import { documentTypeService } from '../../services/document-type.service'
import { documentService } from '../../services/document.service'
import { settingsService } from '../../services/settings.service'
import { storagePathService } from '../../services/storage-path.service'
import { tagService } from '../../services/tag.service'
import { toastService } from '../../services/toast.service'
import { CustomField } from '../../types'
import { flattenTags } from '../../utils/flattenTags'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { DocumentationDialog } from '../common/DocumentationDialog'
import EmailDocumentDialog from '../common/EmailDocumentDialog'
import { FilterableDropdown, FilterableDropdownSelectionModel } from '../common/FilterableDropdown'
import { MergeConfirmDialog } from '../common/MergeConfirmDialog'
import { PermissionsDialog, PermissionsFormData } from '../common/PermissionsDialog'
import { RotateConfirmDialog } from '../common/RotateConfirmDialog'
import './BulkEditor.scss'
import { CustomFieldsBulkEditDialog } from './CustomFieldsBulkEditDialog'

interface BulkEditorProps {
  hidden?: boolean
  disabled?: boolean
}

const BulkEditor: React.FC<BulkEditorProps> = ({ hidden = false, disabled = false }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [awaitingDownload, setAwaitingDownload] = useState(false)
  const [downloadFileTypeArchive, setDownloadFileTypeArchive] = useState(true)
  const [downloadFileTypeOriginals, setDownloadFileTypeOriginals] = useState(false)
  const [downloadUseFormatting, setDownloadUseFormatting] = useState(false)

  // UI Settings
  const showConfirmationDialogs = settingsService.get(SETTINGS_KEYS.BULK_EDIT_CONFIRMATION_DIALOGS) !== false
  const applyOnClose = settingsService.get(SETTINGS_KEYS.BULK_EDIT_APPLY_ON_CLOSE) !== false

  // Selection models for filterable dropdowns
  const [tagSelectionModel] = useState(() => new FilterableDropdownSelectionModel(true))
  const [correspondentSelectionModel] = useState(() => new FilterableDropdownSelectionModel())
  const [documentTypeSelectionModel] = useState(() => new FilterableDropdownSelectionModel())
  const [storagePathSelectionModel] = useState(() => new FilterableDropdownSelectionModel())
  const [customFieldsSelectionModel] = useState(() => new FilterableDropdownSelectionModel(true))

  // Force re-render when models change
  const [, forceUpdate] = useState({})

  // Document counts for each dropdown
  const [tagDocumentCounts, setTagDocumentCounts] = useState<{ [key: number]: number }>({})
  const [correspondentDocumentCounts, setCorrespondentDocumentCounts] = useState<{ [key: number]: number }>({})
  const [documentTypeDocumentCounts, setDocumentTypeDocumentCounts] = useState<{ [key: number]: number }>({})
  const [storagePathDocumentCounts, setStoragePathDocumentCounts] = useState<{ [key: number]: number }>({})
  const [customFieldDocumentCounts, setCustomFieldDocumentCounts] = useState<{ [key: number]: number }>({})

  const [showBulkDocs, setShowBulkDocs] = useState(false)

  // Confirm dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('')
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('')
  const [confirmAction, setConfirmAction] = useState<((permanentDelete?: boolean) => void) | null>(null)
  const [showPermanentDeleteOption, setShowPermanentDeleteOption] = useState(false)
  const [permanentDeleteLabel, setPermanentDeleteLabel] = useState('')
  const [permanentDeleteDescription, setPermanentDeleteDescription] = useState('')

  // Permissions dialog state
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)

  // Rotate dialog state
  const [showRotateDialog, setShowRotateDialog] = useState(false)

  // Merge dialog state
  const [showMergeDialog, setShowMergeDialog] = useState(false)

  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [hasArchiveVersion, setHasArchiveVersion] = useState(true)

  // Custom Fields dialog state
  const [showCustomFieldsDialog, setShowCustomFieldsDialog] = useState(false)
  const [customFieldsToEdit, setCustomFieldsToEdit] = useState<CustomField[]>([])
  const [customFieldsToRemove, setCustomFieldsToRemove] = useState<CustomField[]>([])

  const selectedCount = documentListViewService.selected.size

  // Load all data on mount
  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Load tags
        const tags = await tagService.listAll()
        tagSelectionModel.items = flattenTags(tags)

        // Load correspondents
        const correspondents = await correspondentService.listAll()
        correspondentSelectionModel.items = Array.isArray(correspondents) ? correspondents : correspondents.results || []

        // Load document types
        const documentTypes = await documentTypeService.listAll()
        documentTypeSelectionModel.items = Array.isArray(documentTypes) ? documentTypes : documentTypes.results || []

        // Load storage paths
        const storagePaths = await storagePathService.listAll()
        storagePathSelectionModel.items = Array.isArray(storagePaths) ? storagePaths : storagePaths.results || []

        const customFields = await customFieldService.listAll()
        const customFieldItems = Array.isArray(customFields) ? customFields : customFields.results || []
        customFieldsSelectionModel.items = customFieldItems
      } catch (error) {}
    }

    loadAllData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Helper to apply selection data from backend
  const applySelectionData = (items: { id: number; document_count: number }[], selectionModel: any) => {
    const selectionData = new Map<number, any>()
    const selectedCount = documentListViewService.selected.size

    items.forEach((item) => {
      if (item.document_count === selectedCount) {
        // Svi selektovani dokumenti imaju ovu vrednost - Selected
        selectionData.set(item.id, 1) // ToggleableItemState.Selected
      } else if (item.document_count > 0) {
        // Neki dokumenti imaju ovu vrednost - PartiallySelected
        selectionData.set(item.id, 2) // ToggleableItemState.PartiallySelected
      }
    })

    selectionModel.init(selectionData)
  }

  // Load selection data when dropdowns open
  const loadTagsSelectionData = async () => {
    try {
      const selectionData = await documentService.getSelectionData(Array.from(documentListViewService.selected))

      // Set document counts
      const counts: { [key: number]: number } = {}
      selectionData.selected_tags.forEach((item) => {
        counts[item.id] = item.document_count
      })
      setTagDocumentCounts(counts)

      // Update items with document counts from selection data
      const updatedItems = tagSelectionModel.items.map((item) => {
        const selectionDataItem = selectionData.selected_tags.find((sd) => sd.id === item.id)
        if (selectionDataItem) {
          return {
            ...item,
            document_count: selectionDataItem.document_count,
          }
        }
        return { ...item, document_count: 0 }
      })
      tagSelectionModel.items = updatedItems

      applySelectionData(selectionData.selected_tags, tagSelectionModel)
      forceUpdate({})
    } catch (error) {}
  }

  const loadCorrespondentsSelectionData = async () => {
    try {
      const selectionData = await documentService.getSelectionData(Array.from(documentListViewService.selected))

      // Set document counts
      const counts: { [key: number]: number } = {}
      selectionData.selected_correspondents.forEach((item) => {
        counts[item.id] = item.document_count
      })
      setCorrespondentDocumentCounts(counts)

      // Update items with document counts from selection data
      const updatedItems = correspondentSelectionModel.items.map((item) => {
        const selectionDataItem = selectionData.selected_correspondents.find((sd) => sd.id === item.id)
        if (selectionDataItem) {
          return {
            ...item,
            document_count: selectionDataItem.document_count,
          }
        }
        return { ...item, document_count: 0 }
      })
      correspondentSelectionModel.items = updatedItems

      applySelectionData(selectionData.selected_correspondents, correspondentSelectionModel)
      forceUpdate({})
    } catch (error) {}
  }

  const loadDocumentTypesSelectionData = async () => {
    try {
      const selectionData = await documentService.getSelectionData(Array.from(documentListViewService.selected))

      // Set document counts
      const counts: { [key: number]: number } = {}
      selectionData.selected_document_types.forEach((item) => {
        counts[item.id] = item.document_count
      })
      setDocumentTypeDocumentCounts(counts)

      // Update items with document counts from selection data
      const updatedItems = documentTypeSelectionModel.items.map((item) => {
        const selectionDataItem = selectionData.selected_document_types.find((sd) => sd.id === item.id)
        if (selectionDataItem) {
          return {
            ...item,
            document_count: selectionDataItem.document_count,
          }
        }
        return { ...item, document_count: 0 }
      })
      documentTypeSelectionModel.items = updatedItems

      applySelectionData(selectionData.selected_document_types, documentTypeSelectionModel)
      forceUpdate({})
    } catch (error) {}
  }

  const loadStoragePathsSelectionData = async () => {
    try {
      const selectionData = await documentService.getSelectionData(Array.from(documentListViewService.selected))

      // Set document counts
      const counts: { [key: number]: number } = {}
      selectionData.selected_storage_paths.forEach((item) => {
        counts[item.id] = item.document_count
      })
      setStoragePathDocumentCounts(counts)

      // Update items with document counts from selection data
      const updatedItems = storagePathSelectionModel.items.map((item) => {
        const selectionDataItem = selectionData.selected_storage_paths.find((sd) => sd.id === item.id)
        if (selectionDataItem) {
          return {
            ...item,
            document_count: selectionDataItem.document_count,
          }
        }
        return { ...item, document_count: 0 }
      })
      storagePathSelectionModel.items = updatedItems

      applySelectionData(selectionData.selected_storage_paths, storagePathSelectionModel)
      forceUpdate({})
    } catch (error) {}
  }

  const loadCustomFieldsSelectionData = async () => {
    try {
      const selectionData = await documentService.getSelectionData(Array.from(documentListViewService.selected))

      const counts: { [key: number]: number } = {}
      if (selectionData.selected_custom_fields) {
        selectionData.selected_custom_fields.forEach((item: any) => {
          counts[item.id] = item.document_count
        })
      }
      setCustomFieldDocumentCounts(counts)

      if (selectionData.selected_custom_fields) {
        const updatedItems = customFieldsSelectionModel.items.map((item) => {
          const selectionDataItem = selectionData.selected_custom_fields.find((sd: any) => sd.id === item.id)
          if (selectionDataItem) {
            return {
              ...item,
              document_count: selectionDataItem.document_count,
            }
          }
          return { ...item, document_count: 0 }
        })
        customFieldsSelectionModel.items = updatedItems
      }

      if (selectionData.selected_custom_fields) {
        applySelectionData(selectionData.selected_custom_fields, customFieldsSelectionModel)
      }
      forceUpdate({})
    } catch (error) {}
  }

  // Ensure at least one download type is selected
  useEffect(() => {
    if (!downloadFileTypeArchive && !downloadFileTypeOriginals) {
      setDownloadFileTypeOriginals(true)
    }
  }, [downloadFileTypeArchive, downloadFileTypeOriginals])

  // Helper function to localize list of items
  const localizeList = (items: any[]) => {
    if (items.length === 1) {
      return `"${items[0].name}"`
    } else if (items.length === 2) {
      return `"${items[0].name}" ${t('and')} "${items[1].name}"`
    } else {
      const allButLast = items
        .slice(0, items.length - 1)
        .map((i) => `"${i.name}"`)
        .join(', ')
      return `${allButLast} ${t('and')} "${items[items.length - 1].name}"`
    }
  }

  // Execute bulk operation after confirmation
  const executeBulkOperation = async (
    operation: string,
    params: any,
    options: { onError?: (error: any) => void; showGenericErrorToast?: boolean } = {}
  ): Promise<boolean> => {
    const { onError, showGenericErrorToast = true } = options
    try {
      const documentIds = Array.from(documentListViewService.selected)
      await documentService.bulkEdit(documentIds, {
        method: operation,
        parameters: params,
      } as any)

      for (const id of documentIds) {
        void queryClient.invalidateQueries({ queryKey: ['document-history', id] })
      }

      // Reload document list
      documentListViewService.reload()

      // Clear selection
      documentListViewService.selectNone()
      return true
    } catch (error) {
      onError?.(error)
      if (showGenericErrorToast) {
        toastService.showError(t('error_executing_operation'), error)
      }
      return false
    }
  }

  // Handle tag changes
  const handleSetTags = (changedTags: any) => {
    const itemsToAdd = changedTags.itemsToAdd || []
    const itemsToRemove = changedTags.itemsToRemove || []

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) {
      return
    }

    const executeOperation = () => {
      executeBulkOperation('modify_tags', {
        add_tags: itemsToAdd.map((t: any) => t.id),
        remove_tags: itemsToRemove.map((t: any) => t.id),
      })
      setShowConfirmDialog(false)
    }

    if (showConfirmationDialogs) {
      let message = ''

      if (itemsToAdd.length === 1 && itemsToRemove.length === 0) {
        message = t('this_operation_will_add_the_tag__to_selected_docum', {
          tag: itemsToAdd[0].name,
          count: selectedCount,
        })
      } else if (itemsToAdd.length > 1 && itemsToRemove.length === 0) {
        message = t('this_operation_will_add_the_tags_to_selected_docum', {
          tags: localizeList(itemsToAdd),
          count: selectedCount,
        })
      } else if (itemsToAdd.length === 0 && itemsToRemove.length === 1) {
        message = t('this_operation_will_remove_the_tag__from_selected', {
          tag: itemsToRemove[0].name,
          count: selectedCount,
        })
      } else if (itemsToAdd.length === 0 && itemsToRemove.length > 1) {
        message = t('this_operation_will_remove_the_tags_from_selected', {
          tags: localizeList(itemsToRemove),
          count: selectedCount,
        })
      } else {
        message = t('this_operation_will_add_the_tags_and_remove_the_ta', {
          addTags: localizeList(itemsToAdd),
          removeTags: localizeList(itemsToRemove),
          count: selectedCount,
        })
      }

      setConfirmDialogTitle(t('confirm_tags_assignment'))
      setConfirmDialogMessage(message)
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  const handleDownload = async () => {
    setAwaitingDownload(true)
    try {
      const downloadFileType = downloadFileTypeArchive && downloadFileTypeOriginals ? 'both' : downloadFileTypeArchive ? 'archive' : 'originals'

      const selectedIds = Array.from(documentListViewService.selected)
      const blob = await documentService.bulkDownload(
        selectedIds,
        downloadFileType as 'archive' | 'originals' | 'both',
        downloadUseFormatting
      )

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'documents.zip'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      for (const id of selectedIds) {
        void queryClient.invalidateQueries({ queryKey: ['document-history', id] })
      }
    } catch (error) {
      toastService.showError(t('error_downloading_documents'))
    } finally {
      setAwaitingDownload(false)
    }
  }

  // Handle correspondent changes
  const handleSetCorrespondent = (changedItems: any) => {
    const itemsToAdd = changedItems.itemsToAdd || []
    const itemsToRemove = changedItems.itemsToRemove || []

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) return

    const executeOperation = () => {
      executeBulkOperation('set_correspondent', {
        correspondent: itemsToAdd.length > 0 ? itemsToAdd[0].id : null,
      })
      setShowConfirmDialog(false)
    }

    if (showConfirmationDialogs && itemsToAdd.length > 0) {
      setConfirmDialogTitle(t('confirm_correspondent_assignment'))
      setConfirmDialogMessage(
        t('this_operation_will_assign_the_correspondent__to_s', {
          correspondent: itemsToAdd[0].name,
          count: selectedCount,
        })
      )
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  // Handle document type changes
  const handleSetDocumentType = (changedItems: any) => {
    const itemsToAdd = changedItems.itemsToAdd || []
    const itemsToRemove = changedItems.itemsToRemove || []

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) return

    const executeOperation = () => {
      executeBulkOperation('set_document_type', {
        document_type: itemsToAdd.length > 0 ? itemsToAdd[0].id : null,
      })
      setShowConfirmDialog(false)
    }

    if (showConfirmationDialogs && itemsToAdd.length > 0) {
      setConfirmDialogTitle(t('confirm_document_type_assignment'))
      setConfirmDialogMessage(
        t('this_operation_will_assign_the_document_type__to_s', {
          documentType: itemsToAdd[0].name,
          count: selectedCount,
        })
      )
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  // Handle storage path changes
  const handleSetStoragePath = (changedItems: any) => {
    const itemsToAdd = changedItems.itemsToAdd || []
    const itemsToRemove = changedItems.itemsToRemove || []

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) return

    const executeOperation = () => {
      executeBulkOperation('set_storage_path', {
        storage_path: itemsToAdd.length > 0 ? itemsToAdd[0].id : null,
      })
      setShowConfirmDialog(false)
    }

    if (showConfirmationDialogs && itemsToAdd.length > 0) {
      setConfirmDialogTitle(t('confirm_storage_path_assignment'))
      setConfirmDialogMessage(
        t('this_operation_will_assign_the_storage_path__to_se', {
          storagePath: itemsToAdd[0].name,
          count: selectedCount,
        })
      )
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  const handleSetCustomFields = (changedItems: any) => {
    const itemsToAdd = changedItems.itemsToAdd || []
    const itemsToRemove = changedItems.itemsToRemove || []

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) return

    const executeOperation = () => {
      executeBulkOperation('modify_custom_fields', {
        add_custom_fields: itemsToAdd.map((f: CustomField) => f.id),
        remove_custom_fields: itemsToRemove.map((f: CustomField) => f.id),
      })
      setShowConfirmDialog(false)
    }

    if (showConfirmationDialogs) {
      let message = ''

      if (itemsToAdd.length === 1 && itemsToRemove.length === 0) {
        message = t('this_operation_will_assign_the_custom_field__to_se', {
          field: itemsToAdd[0].name,
          count: selectedCount,
        })
      } else if (itemsToAdd.length > 1 && itemsToRemove.length === 0) {
        message = t('this_operation_will_assign_the_custom_fields_to_se', {
          fields: localizeList(itemsToAdd),
          count: selectedCount,
        })
      } else if (itemsToAdd.length === 0 && itemsToRemove.length === 1) {
        message = t('this_operation_will_remove_the_custom_field__from', {
          field: itemsToRemove[0].name,
          count: selectedCount,
        })
      } else if (itemsToAdd.length === 0 && itemsToRemove.length > 1) {
        message = t('this_operation_will_remove_the_custom_fields_from', {
          fields: localizeList(itemsToRemove),
          count: selectedCount,
        })
      } else {
        message = t('this_operation_will_assign_the_custom_fields_and_r', {
          addFields: localizeList(itemsToAdd),
          removeFields: localizeList(itemsToRemove),
          count: selectedCount,
        })
      }

      setConfirmDialogTitle(t('confirm_custom_fields_assignment'))
      setConfirmDialogMessage(message)
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  const handleSetCustomFieldValues = (changedItems: any) => {
    // Open dialog to set values for selected fields
    const itemsToAdd = changedItems.itemsToAdd || []
    const itemsToRemove = changedItems.itemsToRemove || []

    // Get already assigned custom fields from document counts
    // Fields that have document_count > 0 are already assigned to at least some documents
    const alreadyAssignedFields = customFieldsSelectionModel.items.filter((item) => {
      const documentCount = item.id ? customFieldDocumentCounts[item.id] || 0 : 0
      return documentCount > 0
    }) as CustomField[]

    // Combine new selections with already assigned fields
    const allFieldsToEdit: CustomField[] = []
    const fieldIdsSet = new Set<number>()

    // Add already assigned fields
    alreadyAssignedFields.forEach((field) => {
      if (field.id && !fieldIdsSet.has(field.id)) {
        allFieldsToEdit.push(field)
        fieldIdsSet.add(field.id)
      }
    })

    // Add newly selected fields
    itemsToAdd.forEach((field: CustomField) => {
      if (field.id && !fieldIdsSet.has(field.id)) {
        allFieldsToEdit.push(field)
        fieldIdsSet.add(field.id)
      }
    })

    if (allFieldsToEdit.length === 0 && itemsToRemove.length === 0) {
      toastService.showInfo(t('select_custom_fields'))
      return
    }

    setCustomFieldsToEdit(allFieldsToEdit)
    setCustomFieldsToRemove(itemsToRemove)
    setShowCustomFieldsDialog(true)
  }

  const handleCustomFieldsDialogSuccess = () => {
    // Reload document list
    documentListViewService.reload()

    // Clear selection model
    customFieldsSelectionModel.reset(true)
    forceUpdate({})
  }

  const handleDelete = () => {
    const executeOperation = async (permanentDelete?: boolean) => {
      const selectedIds = Array.from(documentListViewService.selected)
      const ok = await executeBulkOperation('delete', permanentDelete ? { permanent: true } : {}, {
        showGenericErrorToast: false,
        onError: (error: any) => {
          const message =
            error?.response?.status === 403
              ? t('error_deleting_documents_no_permission')
              : t('error_deleting_documents') || 'Greška pri brisanju dokumenata'
          toastService.showError(message, error)
        },
      })

      setShowConfirmDialog(false)
      setShowPermanentDeleteOption(false)

      if (!ok) return

      if (permanentDelete) {
        toastService.showSuccess(
          t('documents_permanently_deleted', { count: selectedIds.length }) || t('documents_deleted', { count: selectedIds.length })
        )
      } else {
        toastService.showSuccess(
          t('documents_moved_to_trash', { count: selectedIds.length }) || t('documents_deleted', { count: selectedIds.length })
        )
      }
    }

    if (showConfirmationDialogs) {
      setConfirmDialogTitle(t('confirm'))
      setConfirmDialogMessage(t('confirm_move_to_trash', { count: selectedCount }))
      setShowPermanentDeleteOption(true)
      setPermanentDeleteLabel(t('permanent_delete'))
      setPermanentDeleteDescription(t('permanent_delete_description'))
      setConfirmAction(() => executeOperation)
      setShowConfirmDialog(true)
    } else {
      executeOperation()
    }
  }

  const handleReprocess = () => {
    const executeOperation = () => {
      executeBulkOperation('reprocess', {})
      setShowConfirmDialog(false)
    }

    setConfirmDialogTitle(t('confirm'))
    setConfirmDialogMessage(t('confirm_reprocess', { count: selectedCount }))
    setConfirmAction(() => executeOperation)
    setShowConfirmDialog(true)
  }

  const handleRotate = () => {
    setShowRotateDialog(true)
  }

  const handleRotateConfirm = (degrees: number) => {
    executeBulkOperation('rotate', { degrees })
    setShowRotateDialog(false)
  }

  const handleMerge = () => {
    if (selectedCount < 2) {
      toastService.showError(t('select_at_least_2_documents'))
      return
    }

    setShowMergeDialog(true)
  }

  const handleMergeConfirm = (metadataDocumentId: number, deleteOriginals: boolean, archiveFallback: boolean, _documentIds: number[]) => {
    const params: any = {
      archive_fallback: archiveFallback,
      delete_originals: deleteOriginals,
    }

    if (metadataDocumentId > -1) {
      params.metadata_document_id = metadataDocumentId
    }

    executeBulkOperation('merge', params)
    setShowMergeDialog(false)
  }

  const handleEmail = () => {
    // Check if all selected documents have archive version
    const allHaveArchiveVersion = documentListViewService.documents
      .filter((d: any) => documentListViewService.selected.has(d.id))
      .every((doc: any) => !!doc.archived_file_name)

    setHasArchiveVersion(allHaveArchiveVersion)
    setShowEmailDialog(true)
  }

  const handleSetPermissions = () => {
    setShowPermissionsDialog(true)
  }

  const handlePermissionsConfirm = async (permissions: PermissionsFormData, merge: boolean) => {
    try {
      const documentIds = Array.from(documentListViewService.selected)
      await documentService.bulkEdit(documentIds, {
        method: 'set_permissions',
        parameters: {
          owner: permissions.owner,
          set_permissions: permissions.set_permissions,
          merge,
        },
      } as any)

      for (const id of documentIds) {
        void queryClient.invalidateQueries({ queryKey: ['document-history', id] })
      }

      setShowPermissionsDialog(false)
      documentListViewService.reload()
      documentListViewService.selectNone()

      toastService.showInfo(t('permissions_updated_successfully') || 'Dozvole su uspešno ažurirane')
    } catch (error) {
      toastService.showError(t('error_updating_permissions'))
    }
  }

  if (hidden) return null

  return (
    <div className="bulk-editor d-flex flex-wrap gap-4">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0">{t('edit')}:</label>
          <button
            type="button"
            className="btn btn-outline-secondary p-0 d-flex align-items-center justify-content-center bulk-editor__info"
            onClick={() => setShowBulkDocs(true)}
            title={t('help')}
          >
            <i className="bi bi-info-circle"></i>
          </button>
        </div>

        {/* Tags Dropdown */}
        <FilterableDropdown
          title={t('tags')}
          icon="tag-fill"
          filterPlaceholder={t('filter_tags')}
          selectionModel={tagSelectionModel}
          onSelectionModelChange={() => forceUpdate({})}
          onOpened={loadTagsSelectionData}
          onApply={handleSetTags}
          documentCounts={tagDocumentCounts}
          disabled={false}
          editing={true}
          applyOnClose={applyOnClose}
          shortcutKey="t"
        />

        {/* Correspondent Dropdown */}
        <FilterableDropdown
          title={t('correspondent')}
          icon="person-fill"
          filterPlaceholder={t('filter_correspondents')}
          selectionModel={correspondentSelectionModel}
          onSelectionModelChange={() => forceUpdate({})}
          onOpened={loadCorrespondentsSelectionData}
          onApply={handleSetCorrespondent}
          documentCounts={correspondentDocumentCounts}
          disabled={false}
          editing={true}
          applyOnClose={applyOnClose}
          shortcutKey="y"
        />

        {/* Document Type Dropdown */}
        <FilterableDropdown
          title={t('document_type')}
          icon="file-earmark-fill"
          filterPlaceholder={t('filter_document_types')}
          selectionModel={documentTypeSelectionModel}
          onSelectionModelChange={() => forceUpdate({})}
          onOpened={loadDocumentTypesSelectionData}
          onApply={handleSetDocumentType}
          documentCounts={documentTypeDocumentCounts}
          disabled={false}
          editing={true}
          applyOnClose={applyOnClose}
          shortcutKey="u"
        />

        {/* Storage Path Dropdown */}
        <FilterableDropdown
          title={t('storage_path')}
          icon="folder-fill"
          filterPlaceholder={t('filter_storage_paths')}
          selectionModel={storagePathSelectionModel}
          onSelectionModelChange={() => forceUpdate({})}
          onOpened={loadStoragePathsSelectionData}
          onApply={handleSetStoragePath}
          documentCounts={storagePathDocumentCounts}
          disabled={false}
          editing={true}
          applyOnClose={applyOnClose}
          shortcutKey="i"
        />

        {/* Custom Fields Dropdown */}
        <FilterableDropdown
          title={t('custom_fields')}
          icon="ui-radios"
          filterPlaceholder={t('filter_custom_fields')}
          selectionModel={customFieldsSelectionModel}
          onSelectionModelChange={() => forceUpdate({})}
          onOpened={loadCustomFieldsSelectionData}
          onApply={handleSetCustomFields}
          documentCounts={customFieldDocumentCounts}
          disabled={false}
          editing={true}
          applyOnClose={applyOnClose}
          extraButtonTitle={t('set_values') || 'Postavi vrednosti'}
          onExtraButton={handleSetCustomFieldValues}
        />

        {/* Permissions Button */}
        <ButtonGroup>
          <Button variant="outline-primary" size="sm" onClick={handleSetPermissions} disabled={disabled} className="me-2">
            <i className="bi bi-person-fill-lock"></i>
            <span className="d-none d-sm-inline">&nbsp;{t('permissions')}</span>
          </Button>
        </ButtonGroup>
      </div>

      <div className="d-flex align-items-center gap-2 ms-auto">
        {/* Actions Dropdown */}
        <ButtonGroup size="sm">
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" size="sm" disabled={disabled}>
              <i className="bi bi-three-dots"></i>
              <span className="d-none d-sm-inline">&nbsp;{t('actions')}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow">
              <Dropdown.Item onClick={handleReprocess}>
                <i className="bi bi-body-text"></i>&nbsp;{t('reprocess')}
              </Dropdown.Item>
              <Dropdown.Item onClick={handleRotate}>
                <i className="bi bi-arrow-clockwise"></i>&nbsp;{t('rotate')}
              </Dropdown.Item>
              <Dropdown.Item onClick={handleMerge} disabled={selectedCount < 2}>
                <i className="bi bi-journals"></i>&nbsp;{t('merge')}
              </Dropdown.Item>
              <Dropdown.Item onClick={handleEmail}>
                <i className="bi bi-envelope"></i>&nbsp;{t('email')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>

        {/* Download Button Group */}
        <ButtonGroup size="sm" className="me-2">
          <Button variant="outline-primary" size="sm" disabled={awaitingDownload || disabled} onClick={handleDownload}>
            {awaitingDownload ? (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">{t('preparing_download')}</span>
              </div>
            ) : (
              <i className="bi bi-arrow-down"></i>
            )}
            <span className="d-none d-sm-inline">&nbsp;{t('download')}</span>
          </Button>

          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle split variant="outline-primary" size="sm" className="rounded-end" />
            <Dropdown.Menu className="shadow">
              <Form className="px-3 py-1">
                <p className="mb-1">{t('include')}:</p>
                <div className="form-group ps-3 mb-2">
                  <Form.Check
                    type="checkbox"
                    id="downloadFileType_archive"
                    label={t('archived_files')}
                    checked={downloadFileTypeArchive}
                    onChange={(e) => setDownloadFileTypeArchive(e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    id="downloadFileType_originals"
                    label={t('original_files')}
                    checked={downloadFileTypeOriginals}
                    onChange={(e) => setDownloadFileTypeOriginals(e.target.checked)}
                  />
                </div>
                <Form.Check
                  type="checkbox"
                  id="downloadUseFormatting"
                  label={t('use_formatted_filename')}
                  checked={downloadUseFormatting}
                  onChange={(e) => setDownloadUseFormatting(e.target.checked)}
                />
              </Form>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>

        {/* Delete Button */}
        <ButtonGroup size="sm">
          <Button variant="outline-danger" size="sm" onClick={handleDelete} disabled={disabled}>
            <i className="bi bi-trash"></i>&nbsp;{t('delete')}
          </Button>
        </ButtonGroup>
      </div>

      <DocumentationDialog show={showBulkDocs} onHide={() => setShowBulkDocs(false)} docKey="bulk_editing" />

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={showConfirmDialog}
        title={confirmDialogTitle}
        message={confirmDialogMessage}
        cancelText={t('cancel')}
        onConfirm={(permanentDelete?: boolean) => {
          if (confirmAction) {
            confirmAction(permanentDelete)
          }
        }}
        onCancel={() => {
          setShowPermanentDeleteOption(false)
          setShowConfirmDialog(false)
        }}
        variant="warning"
        showPermanentDeleteOption={showPermanentDeleteOption}
        permanentDeleteLabel={permanentDeleteLabel}
        permanentDeleteDescription={permanentDeleteDescription}
      />

      {/* Permissions Dialog */}
      <PermissionsDialog
        show={showPermissionsDialog}
        onHide={() => setShowPermissionsDialog(false)}
        onConfirm={handlePermissionsConfirm}
        selectedCount={selectedCount}
      />

      {/* Rotate Dialog */}
      <RotateConfirmDialog
        show={showRotateDialog}
        onHide={() => setShowRotateDialog(false)}
        onConfirm={handleRotateConfirm}
        documentId={Array.from(documentListViewService.selected)[0]}
      />

      {/* Merge Dialog */}
      <MergeConfirmDialog
        show={showMergeDialog}
        onHide={() => setShowMergeDialog(false)}
        onConfirm={handleMergeConfirm}
        documentIds={Array.from(documentListViewService.selected)}
      />

      {/* Email Dialog */}
      <EmailDocumentDialog
        show={showEmailDialog}
        onHide={() => setShowEmailDialog(false)}
        documentIds={Array.from(documentListViewService.selected)}
        hasArchiveVersion={hasArchiveVersion}
      />

      {/* Custom Fields Bulk Edit Dialog */}
      <CustomFieldsBulkEditDialog
        show={showCustomFieldsDialog}
        onHide={() => setShowCustomFieldsDialog(false)}
        fieldsToEdit={customFieldsToEdit}
        fieldsToRemove={customFieldsToRemove}
        documentIds={Array.from(documentListViewService.selected)}
        onSuccess={handleCustomFieldsDialogSuccess}
      />
    </div>
  )
}

export default BulkEditor
